import { useEffect, useMemo, useState } from 'react'
import { api, money, fmtDate, today, toPaise, fromPaise, ApiError } from '../api'
import { Page } from '../App'
import { Card, Alert, Spinner, Empty, Modal, Field, Stat, Badge, Confirm } from '../components/ui'

interface Expense {
  id: string
  expense_date: string
  category_id: string | null
  category_name: string | null
  description: string
  vendor_name: string | null
  vendor_gstin: string | null
  vendor_state_code: string | null
  has_gst: number
  supplier_invoice_no: string | null
  supplier_invoice_date: string | null
  taxable_value: number
  tax_rate: number
  cgst: number
  sgst: number
  igst: number
  cess: number
  total: number
  itc_eligible: number
  itc_blocked_reason: string | null
  payment_status: string
  paid_on: string | null
  payment_method: string | null
  payment_reference: string | null
  tds_amount: number
  tds_section: string | null
  attachment_key: string | null
  notes: string | null
}

interface Meta {
  categories: { id: string; name: string }[]
  itcBlockedReasons: string[]
  tdsSections: { code: string; label: string; rate: number }[]
  methods: string[]
}

interface Totals {
  n: number
  total: number
  taxable: number
  itc: number
  tds: number
  unpaid: number
}

export default function Expenses() {
  const [rows, setRows] = useState<Expense[]>([])
  const [totals, setTotals] = useState<Totals | null>(null)
  const [meta, setMeta] = useState<Meta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<Expense | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Expense | null>(null)

  const fyStart = useMemo(() => {
    const t = today()
    const y = Number(t.slice(0, 4))
    return Number(t.slice(5, 7)) >= 4 ? `${y}-04-01` : `${y - 1}-04-01`
  }, [])
  const [from, setFrom] = useState(fyStart)
  const [to, setTo] = useState(today())
  const [category, setCategory] = useState('')
  const [q, setQ] = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const p = new URLSearchParams({ from, to })
      if (category) p.set('category', category)
      if (q) p.set('q', q)
      const res = await api.get<{ expenses: Expense[]; totals: Totals }>(`/expenses?${p}`)
      setRows(res.expenses)
      setTotals(res.totals)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load expenses')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    api.get<Meta>('/expenses/meta').then(setMeta).catch(() => {})
  }, [])

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, category, q])

  return (
    <Page
      title="Expenses"
      subtitle="What you spend, and the GST you can claim back on it"
      actions={
        <>
          <a className="btn" href={`/api/reports/expenses.csv?fy=${Number(fyStart.slice(0, 4))}`}>
            Export CSV
          </a>
          <button className="btn primary" onClick={() => setCreating(true)}>
            + New expense
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      {totals && (
        <div className="grid c4" style={{ marginBottom: 16 }}>
          <Stat label="Total spent" value={money(totals.total)} note={`${totals.n} expense${totals.n === 1 ? '' : 's'}`} />
          <Stat label="Cost (excl. GST)" value={money(totals.taxable)} note="This is what hits profit" />
          <Stat
            label="Input tax credit"
            value={money(totals.itc)}
            note="Offsets your GST bill"
            tone={totals.itc > 0 ? 'ok' : undefined}
          />
          <Stat
            label="Unpaid"
            value={money(totals.unpaid)}
            note={totals.tds > 0 ? `${money(totals.tds)} TDS withheld` : 'Nothing outstanding'}
            tone={totals.unpaid > 0 ? 'danger' : undefined}
          />
        </div>
      )}

      <div className="inline" style={{ marginBottom: 14 }}>
        <div>
          <label className="small muted">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="small muted">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label className="small muted">Category</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">All</option>
            {meta?.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="small muted">Search</label>
          <input type="text" placeholder="Vendor, description, bill no…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <Card tight>
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty
            icon="₹"
            title="No expenses in this period"
            hint="Add rent, salaries, software, hosting — anything you spend on the business."
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Vendor</th>
                  <th className="r">Cost</th>
                  <th className="r">GST</th>
                  <th className="r">ITC</th>
                  <th className="r">Total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const gst = e.cgst + e.sgst + e.igst + e.cess
                  return (
                    <tr key={e.id}>
                      <td className="nowrap small">{fmtDate(e.expense_date)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{e.description}</div>
                        {e.payment_status === 'unpaid' && <Badge status="overdue">Unpaid</Badge>}
                        {e.tds_amount > 0 && (
                          <span className="muted small"> TDS {money(e.tds_amount)} {e.tds_section}</span>
                        )}
                      </td>
                      <td className="small">{e.category_name ?? '—'}</td>
                      <td className="small">
                        {e.vendor_name ?? '—'}
                        {e.vendor_gstin && <div className="mono muted" style={{ fontSize: 11 }}>{e.vendor_gstin}</div>}
                      </td>
                      <td className="num">{money(e.taxable_value)}</td>
                      <td className="num">{e.has_gst ? money(gst) : <span className="muted">—</span>}</td>
                      <td className="num">
                        {!e.has_gst ? (
                          <span className="muted">—</span>
                        ) : e.itc_eligible ? (
                          <strong style={{ color: 'var(--ok)' }}>{money(gst)}</strong>
                        ) : (
                          <Badge status="draft">Blocked</Badge>
                        )}
                      </td>
                      <td className="num">
                        <strong>{money(e.total)}</strong>
                      </td>
                      <td className="right nowrap">
                        {e.attachment_key && (
                          <a className="btn sm" href={`/api/expenses/attachment/${e.id}`} target="_blank" rel="noreferrer">
                            Bill
                          </a>
                        )}{' '}
                        <button className="btn sm" onClick={() => setEditing(e)}>
                          Edit
                        </button>{' '}
                        <button className="btn sm danger" onClick={() => setDeleting(e)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {(creating || editing) && meta && (
        <ExpenseForm
          expense={editing}
          meta={meta}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            load()
          }}
        />
      )}

      {deleting && (
        <Confirm
          title="Delete this expense?"
          danger
          confirmLabel="Delete"
          message={
            <>
              <strong>{deleting.description}</strong> — {money(deleting.total)}.
              {deleting.has_gst && deleting.itc_eligible ? (
                <> This will also remove {money(deleting.cgst + deleting.sgst + deleting.igst + deleting.cess)} of input tax credit.</>
              ) : null}
            </>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await api.del(`/expenses/${deleting.id}`)
            setDeleting(null)
            load()
          }}
        />
      )}
    </Page>
  )
}

function ExpenseForm({
  expense,
  meta,
  onClose,
  onSaved,
}: {
  expense: Expense | null
  meta: Meta
  onClose: () => void
  onSaved: () => void
}) {
  const [f, setF] = useState<Record<string, unknown>>(
    expense
      ? {
          ...expense,
          taxable_value: fromPaise(expense.taxable_value),
          tds_amount: fromPaise(expense.tds_amount),
          has_gst: expense.has_gst === 1,
          itc_eligible: expense.itc_eligible === 1,
        }
      : {
          expense_date: today(),
          payment_status: 'paid',
          payment_method: 'upi',
          has_gst: false,
          itc_eligible: true,
          tax_rate: 18,
          tds_amount: '0',
        },
  )
  const [vendors, setVendors] = useState<{ name: string; gstin: string | null; state_code: string | null }[]>([])
  const [error, setError] = useState('')
  const [field, setField] = useState<string | undefined>()
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    api.get<{ vendors: typeof vendors }>('/expenses/vendors').then((r) => setVendors(r.vendors)).catch(() => {})
  }, [])

  const set = (k: string, v: unknown) => setF((p) => ({ ...p, [k]: v }))
  const hasGst = Boolean(f.has_gst)
  const taxable = toPaise(String(f.taxable_value ?? '0'))
  const rate = Number(f.tax_rate ?? 0)

  // Mirror of the server's rule, purely so the preview matches what will be
  // saved. The server recomputes and its answer is the one stored.
  const vendorState = String(f.vendor_state_code ?? '')
  const intra = Boolean(vendorState) && vendorState === '10'
  const gstAmount = hasGst ? Math.round((taxable * rate) / 100) : 0
  const total = taxable + gstAmount

  const save = async () => {
    setError('')
    setField(undefined)
    setBusy(true)
    try {
      const body = { ...f, taxable_value: String(f.taxable_value ?? '0'), tds_amount: String(f.tds_amount ?? '0') }
      if (expense) await api.put(`/expenses/${expense.id}`, body)
      else await api.post('/expenses', body)
      onSaved()
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message)
        setField(e.field)
      } else setError('Save failed')
    } finally {
      setBusy(false)
    }
  }

  const upload = async (file: File) => {
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await api.post<{ key: string }>('/expenses/upload', fd)
      set('attachment_key', r.key)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal
      wide
      title={expense ? 'Edit expense' : 'New expense'}
      onClose={onClose}
      footer={
        <>
          <span className="small muted" style={{ marginRight: 'auto' }}>
            Total {money(total)}
            {hasGst && gstAmount > 0 && ` · ${intra ? 'CGST+SGST' : 'IGST'} ${money(gstAmount)}`}
          </span>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save expense'}
          </button>
        </>
      }
    >
      <Alert kind="error">{error}</Alert>

      <div className="grid c2">
        <Field label="Date">
          <input type="date" value={String(f.expense_date ?? '')} onChange={(e) => set('expense_date', e.target.value)} />
        </Field>
        <Field label="Category">
          <select value={String(f.category_id ?? '')} onChange={(e) => set('category_id', e.target.value)}>
            <option value="">Uncategorised</option>
            {meta.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="What was it for?" error={field === 'description' ? error : undefined}>
        <input
          type="text"
          value={String(f.description ?? '')}
          onChange={(e) => set('description', e.target.value)}
          placeholder="Office rent for October"
        />
      </Field>

      <Field label="Vendor" hint="Previously used vendors are suggested.">
        <input
          type="text"
          list="vendor-list"
          value={String(f.vendor_name ?? '')}
          onChange={(e) => {
            const v = vendors.find((x) => x.name === e.target.value)
            set('vendor_name', e.target.value)
            if (v) {
              if (v.gstin) set('vendor_gstin', v.gstin)
              if (v.state_code) set('vendor_state_code', v.state_code)
              if (v.gstin) set('has_gst', true)
            }
          }}
        />
        <datalist id="vendor-list">
          {vendors.map((v) => (
            <option key={v.name} value={v.name} />
          ))}
        </datalist>
      </Field>

      <div className="sep" />

      <label className="check" style={{ marginBottom: 10 }}>
        <input type="checkbox" checked={hasGst} onChange={(e) => set('has_gst', e.target.checked)} />
        <strong>This is a GST bill</strong>
        <span className="muted small"> — tick it to claim input tax credit</span>
      </label>

      <div className="grid c2">
        <Field label={hasGst ? 'Taxable value (before GST)' : 'Amount'} error={field === 'taxable_value' ? error : undefined}>
          <input
            type="number"
            step="0.01"
            value={String(f.taxable_value ?? '')}
            onChange={(e) => set('taxable_value', e.target.value)}
            autoFocus
          />
        </Field>
        {hasGst && (
          <Field label="GST rate (%)">
            <select value={String(f.tax_rate ?? 18)} onChange={(e) => set('tax_rate', Number(e.target.value))}>
              {[0, 5, 12, 18, 28].map((r) => (
                <option key={r} value={r}>
                  {r}%
                </option>
              ))}
            </select>
          </Field>
        )}
      </div>

      {hasGst && (
        <>
          <div className="grid c2">
            <Field
              label="Vendor GSTIN"
              hint="Decides CGST+SGST vs IGST, and is checksum-validated."
              error={field === 'vendor_gstin' ? error : undefined}
            >
              <input
                type="text"
                className="mono"
                maxLength={15}
                value={String(f.vendor_gstin ?? '')}
                onChange={(e) => set('vendor_gstin', e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Vendor's bill number" hint="Needed to match against GSTR-2B.">
              <input
                type="text"
                value={String(f.supplier_invoice_no ?? '')}
                onChange={(e) => set('supplier_invoice_no', e.target.value)}
              />
            </Field>
          </div>

          <label className="check" style={{ marginBottom: 10 }}>
            <input
              type="checkbox"
              checked={Boolean(f.itc_eligible)}
              onChange={(e) => set('itc_eligible', e.target.checked)}
            />
            Input tax credit can be claimed on this
          </label>

          {!f.itc_eligible && (
            <Field label="Why is credit blocked?" hint="Recorded so the gap is explainable at filing time.">
              <select
                value={String(f.itc_blocked_reason ?? '')}
                onChange={(e) => set('itc_blocked_reason', e.target.value)}
              >
                <option value="">Select a reason…</option>
                {meta.itcBlockedReasons.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </>
      )}

      <div className="sep" />

      <div className="grid c3">
        <Field label="Payment status">
          <select value={String(f.payment_status ?? 'paid')} onChange={(e) => set('payment_status', e.target.value)}>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
        </Field>
        <Field label="Method">
          <select value={String(f.payment_method ?? '')} onChange={(e) => set('payment_method', e.target.value)}>
            <option value="">—</option>
            {meta.methods.map((m) => (
              <option key={m} value={m}>
                {m.toUpperCase()}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Reference / UTR">
          <input
            type="text"
            className="mono"
            value={String(f.payment_reference ?? '')}
            onChange={(e) => set('payment_reference', e.target.value)}
          />
        </Field>
      </div>

      <div className="grid c2">
        <Field label="TDS deducted" hint="What you withheld from the vendor, if any." error={field === 'tds_amount' ? error : undefined}>
          <input type="number" step="0.01" value={String(f.tds_amount ?? '0')} onChange={(e) => set('tds_amount', e.target.value)} />
        </Field>
        <Field label="TDS section">
          <select value={String(f.tds_section ?? '')} onChange={(e) => set('tds_section', e.target.value)}>
            <option value="">—</option>
            {meta.tdsSections.map((s) => (
              <option key={s.code} value={s.code}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {toPaise(String(f.tds_amount ?? '0')) > 0 && (
        <div className="alert info small">
          Vendor receives {money(total - toPaise(String(f.tds_amount ?? '0')))} after TDS of{' '}
          {money(toPaise(String(f.tds_amount ?? '0')))}. The deduction is payable to the government.
        </div>
      )}

      <Field label="Bill / receipt" hint="PNG, JPEG, WebP or PDF, under 5 MB.">
        <input type="file" accept="image/*,application/pdf" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        {uploading && <div className="hint">Uploading…</div>}
        {Boolean(f.attachment_key) && <div className="hint" style={{ color: 'var(--ok)' }}>✓ Attached</div>}
      </Field>

      <Field label="Notes">
        <textarea value={String(f.notes ?? '')} onChange={(e) => set('notes', e.target.value)} />
      </Field>
    </Modal>
  )
}
